import { useState, useEffect, useContext } from "react";
import { supabase } from "../supabase";
import { CartContext, AuthContext, BrandContext, SiteSettingsContext } from "../contexts";
import { Btn } from "../components/ui";
import { getPrimaryImage, validateCoupon } from "../utils/helpers";
import { resolveDiscountCode } from "../utils/resolveDiscountCode";
import { priceCart } from "../utils/bulkPricing";
import AddressInput from "../components/AddressAutocomplete";
import { runCheckoutWorkflow } from "../checkout-workflow";
import { getErrorBranch } from "../checkout-errors";
import { insertAddressDeduped } from "../utils/addresses";
import { composeName, NAME_SUFFIXES } from "../utils/names";

const ManualToggle = ({ active, onToggle }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: -4 }}>
    <button
      type="button"
      onClick={onToggle}
      style={{
        background: "none", border: "none", padding: "2px 0",
        fontSize: "11px", color: active ? "var(--green)" : "var(--amber)", cursor: "pointer",
        fontFamily: "var(--font-mono)", letterSpacing: "0.03em",
      }}
    >
      {active ? "✓ Manual" : "Manual Entry"}
    </button>
  </div>
);

let warnedAboutLegacyTotals = false;

// Name-parts grid (module scope so input identity is stable across renders).
// Writes the parts; callers derive the display name via composeName (#38).
const NamePartsFields = ({ value, onChange }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <input autoComplete="given-name" value={value.firstName || ""} placeholder="First name *"
        onChange={e => onChange({ firstName: e.target.value })} />
      <input autoComplete="family-name" value={value.lastName || ""} placeholder="Last name *"
        onChange={e => onChange({ lastName: e.target.value })} />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: 12 }}>
      <input autoComplete="additional-name" value={value.middleName || ""} placeholder="Middle name (optional)"
        onChange={e => onChange({ middleName: e.target.value })} />
      <select autoComplete="honorific-suffix" value={value.suffix || ""} onChange={e => onChange({ suffix: e.target.value })}>
        {NAME_SUFFIXES.map(sfx => <option key={sfx} value={sfx}>{sfx || "Suffix (opt)"}</option>)}
      </select>
    </div>
  </div>
);

const CheckoutView = ({ setView, setInitialAccountTab }) => {
  const { cart, dispatch, appliedCoupon, setAppliedCoupon } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const b = useContext(BrandContext);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: user?.name || "", firstName: "", middleName: "", lastName: "", suffix: "", email: user?.email || "", address: "", address2: "", city: "", state: "", zip: "", card: "", exp: "", cvv: "" });

  // Prefill structured name parts from the profile (backfilled by #38 Phase 1)
  useEffect(() => {
    if (!user?.id) return;
    supabase.from("profiles").select("first_name, middle_name, last_name, suffix").eq("id", user.id).single()
      .then(({ data }) => {
        if (data) setForm(f => ({ ...f,
          firstName: f.firstName || data.first_name || "", middleName: f.middleName || data.middle_name || "",
          lastName: f.lastName || data.last_name || "", suffix: f.suffix || data.suffix || "" }));
      });
  }, [user?.id]);

  const setShipName = (patch) => setForm(f => { const n = { ...f, ...patch }; return { ...n, name: composeName(n) }; });
  const setBillName = (patch) => setBillingForm(f => { const n = { ...f, ...patch }; return { ...n, name: composeName(n) }; });
  const freeThreshold = b.freeShippingEnabled === false ? Infinity : (b.freeShippingThreshold ?? 100);
  const pricing = priceCart({ items: cart, coupon: appliedCoupon, freeThreshold, flatShippingRate: b.flatShippingRate ?? 0, freeShippingEnabled: b.freeShippingEnabled });
  const { subtotal, bulkSavings, discountedUnits, eligibleUnits, postBulk, couponDiscount: discount, shipping, totalBeforeCredit } = pricing;

  // Store credit
  const [storeCreditBalance, setStoreCreditBalance] = useState(0);
  const [useStoreCredit, setUseStoreCredit] = useState(false);
  const storeCreditToApply = useStoreCredit ? Math.min(storeCreditBalance, totalBeforeCredit) : 0;
  const total = Math.max(0, totalBeforeCredit - storeCreditToApply);
  const [confirmedTotals, setConfirmedTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponInputError, setCouponInputError] = useState("");
  const [couponInputLoading, setCouponInputLoading] = useState(false);

  // Affiliate auto-apply is handled by CartModal (which mounts first).
  // Removed duplicate useEffect here to prevent double-applying the discount.

  const handleApplyCouponCheckout = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponInputLoading(true);
    setCouponInputError("");

    const result = await resolveDiscountCode(code, postBulk);
    setCouponInputLoading(false);
    if (result.error) { setCouponInputError(result.error); return; }
    setAppliedCoupon(result.coupon);
    if (result.coupon.isAffiliate) localStorage.setItem("_ref", result.coupon.code);
    setCouponInput("");
  };

  // Saved addresses
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [addressMode, setAddressMode] = useState("saved"); // "saved" or "new"
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [shippingError, setShippingError] = useState("");
  const [saveNewAddr, setSaveNewAddr] = useState(false);
  const [newAddrLabel, setNewAddrLabel] = useState("");
  const [shipManualEntry, setShipManualEntry] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("addresses")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at")
      .then(({ data }) => {
        if (data && data.length > 0) {
          // Sort: default address first, then the rest by created_at
          const sorted = [...data].sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0));
          setSavedAddresses(sorted);
          const defaultAddr = sorted[0];
          setSelectedAddrId(defaultAddr.id);
          setForm(f => ({ ...f, name: defaultAddr.name, address: defaultAddr.street, address2: defaultAddr.street2 || "", city: defaultAddr.city, state: defaultAddr.state, zip: defaultAddr.zip }));
        } else {
          setAddressMode("new");
        }
        setAddressesLoading(false);
      }).catch(err => { console.error("Failed to load addresses:", err); setAddressesLoading(false); setAddressMode("new"); });
  }, [user?.id]);

  // Fetch store credit balance
  useEffect(() => {
    if (!user?.id) return;
    supabase.rpc("get_store_credit_balance", { p_user_id: user.id })
      .then(({ data, error }) => { if (!error && data != null) setStoreCreditBalance(Number(data)); })
      .catch(err => console.error("Failed to load store credit:", err));
  }, [user?.id]);

  const selectAddress = (addr) => {
    setSelectedAddrId(addr.id);
    setForm(f => ({ ...f, name: addr.name, address: addr.street, address2: addr.street2 || "", city: addr.city, state: addr.state, zip: addr.zip }));
  };

  const validateAndSaveShipping = async () => {
    if (addressMode === "saved" && !selectedAddrId) { setShippingError("Please select an address."); return false; }
    if (addressMode === "new") {
      const missing = [
        !form.firstName.trim() && "First name",
        !form.lastName.trim() && "Last name",
        !form.address.trim() && "Street address",
        !form.city.trim() && "City",
        !form.state.trim() && "State",
        !form.zip.trim() && "ZIP",
      ].filter(Boolean);
      if (missing.length > 0) {
        setShippingError(`Required: ${missing.join(", ")}`); return false;
      }
      if (form.state.trim().length !== 2) { setShippingError("State must be a 2-letter abbreviation."); return false; }
      if (!/^\d{5}$/.test(form.zip.trim())) { setShippingError("ZIP must be 5 digits."); return false; }
      if (saveNewAddr && !newAddrLabel.trim()) { setShippingError("Please enter a label for the address (e.g. Home, Work)."); return false; }

      // Save the new address. A logged-in user's FIRST address is always saved
      // as the default — even without opting in — so everyone ends up with a
      // default on file; they can rename the "Default" label in their profile.
      // Additional addresses save only when "Save this address" is checked.
      const isFirst = savedAddresses.length === 0;
      if ((saveNewAddr || isFirst) && user?.id) {
        const { error } = await insertAddressDeduped({
          label: newAddrLabel.trim() || "Default",
          name: form.name.trim(),
          first_name: form.firstName.trim() || null,
          middle_name: form.middleName.trim() || null,
          last_name: form.lastName.trim() || null,
          suffix: form.suffix.trim() || null,
          street: form.address.trim(),
          street2: form.address2.trim() || null,
          city: form.city.trim(),
          state: form.state.trim(),
          zip: form.zip.trim(),
          user_id: user.id,
          is_default: isFirst,
        });
        if (error) console.error("Failed to save address:", error);
      }
    }
    setShippingError("");
    return true;
  };

  const [paymentMethod, setPaymentMethod] = useState(""); // "venmo" | "cashapp" | "zelle"
  const { settings: siteSettingsData } = useContext(SiteSettingsContext);
  const [paymentHandles, setPaymentHandles] = useState({ venmo_handle: "", cashapp_handle: "", zelle_handle: "", venmo_enabled: false, cashapp_enabled: false, zelle_enabled: false });
  const [paymentError, setPaymentError] = useState("");

  // Billing address (credit card only)
  const [billingMode, setBillingMode] = useState("same"); // "same" | "saved" | "new"
  const [selectedBillingAddrId, setSelectedBillingAddrId] = useState(null);
  const [billingForm, setBillingForm] = useState({ name: "", address: "", address2: "", city: "", state: "", zip: "" });
  const [saveBillingAddr, setSaveBillingAddr] = useState(false);
  const [billingAddrLabel, setBillingAddrLabel] = useState("");
  const [billingError, setBillingError] = useState("");
  const [billManualEntry, setBillManualEntry] = useState(false);

  useEffect(() => {
    if (siteSettingsData) {
      const handles = { venmo_handle: siteSettingsData.venmo_handle ?? "", cashapp_handle: siteSettingsData.cashapp_handle ?? "", zelle_handle: siteSettingsData.zelle_handle ?? "", venmo_enabled: siteSettingsData.venmo_enabled ?? false, cashapp_enabled: siteSettingsData.cashapp_enabled ?? false, zelle_enabled: siteSettingsData.zelle_enabled ?? false };
      setPaymentHandles(handles);
      // Default to first enabled payment method
      if (!paymentMethod) {
        const first = [
          { key: "venmo", handle: handles.venmo_handle, enabled: handles.venmo_enabled },
          { key: "cashapp", handle: handles.cashapp_handle, enabled: handles.cashapp_enabled },
          { key: "zelle", handle: handles.zelle_handle, enabled: handles.zelle_enabled },
        ].find(m => m.handle && m.enabled);
        if (first) setPaymentMethod(first.key);
      }
    }
  }, [siteSettingsData]); // eslint-disable-line react-hooks/exhaustive-deps

  const [orderError, setOrderError] = useState(null); // { title, message, recoveryAction, recoveryLabel, retryable }

  const handlePlaceOrder = async () => {
    setLoading(true);
    setOrderError(null);

    // Re-validate coupon at order time
    if (appliedCoupon) {
      const v = validateCoupon(appliedCoupon, postBulk);
      if (!v.valid) {
        setOrderError({ title: "Coupon expired", message: v.reason, recoveryAction: "retry", recoveryLabel: "Try again", retryable: true });
        setAppliedCoupon(null);
        setLoading(false);
        return;
      }
    }

    const orderId = "ORD-" + Date.now();

    // Run checkout workflow — critical steps rollback on failure, non-critical steps are fire-and-forget
    const result = await runCheckoutWorkflow({
      orderId,
      user,
      cart,
      totals: { subtotal, bulkSavings, discount, shipping, total, totalBeforeCredit },
      appliedCoupon,
      paymentMethod,
      resolvedAddress,
      resolvedBillingAddress,
      paymentHandles,
      storeCreditToApply,
    });

    if (!result.ok) {
      const rawError = result.errors?.find(e => e.critical)?.error;
      const branch = getErrorBranch(result.errorType, rawError);
      setOrderError(branch);
      if (branch.recoveryAction === "back_to_cart") {
        // Out of stock — send them back to cart after a delay
        setTimeout(() => setView("cart"), 3000);
      } else if (branch.recoveryAction === "fix_address") {
        setStep(1);
      } else if (branch.recoveryAction === "change_payment") {
        setStep(2);
      }
      setLoading(false);
      return;
    }

    // Log any non-critical failures for debugging
    if (result.errors) {
      result.errors.forEach(e => console.error(`Checkout step "${e.step}" failed:`, e.error));
    }

    const server = Array.isArray(result.data) ? result.data[0] : result.data;
    const fallback = { merchandise_subtotal: subtotal, bulk_vial_discount: bulkSavings, coupon_discount: discount, shipping, store_credit_applied: storeCreditToApply, total };
    const missing = Object.keys(fallback).filter(field => server?.[field] == null);
    if (missing.length && !warnedAboutLegacyTotals) {
      console.warn(`place_order response missing confirmed total fields: ${missing.join(", ")}; using client fallbacks`);
      warnedAboutLegacyTotals = true;
    }
    setConfirmedTotals({
      subtotal: Number(server?.merchandise_subtotal ?? fallback.merchandise_subtotal),
      bulkSavings: Number(server?.bulk_vial_discount ?? fallback.bulk_vial_discount),
      discount: Number(server?.coupon_discount ?? fallback.coupon_discount),
      shipping: Number(server?.shipping ?? fallback.shipping),
      storeCreditToApply: Number(server?.store_credit_applied ?? fallback.store_credit_applied),
      total: Number(server?.total ?? fallback.total),
      items: cart.map(i => ({ ...i })), coupon: appliedCoupon,
    });
    dispatch({ type: "CLEAR" });
    setAppliedCoupon(null);
    sessionStorage.removeItem("_cid");

    setLoading(false);
    sessionStorage.setItem("lastOrderId", orderId);
    window.umami?.track("order-complete", { total, items: cart.length, method: paymentMethod });
    setStep(4);
  };

  // Build the resolved shipping address for the review step
  const resolvedAddress = (() => {
    if (addressMode === "saved") {
      const addr = savedAddresses.find(a => a.id === selectedAddrId);
      if (addr) return { name: addr.name, first_name: addr.first_name || null, middle_name: addr.middle_name || null, last_name: addr.last_name || null, suffix: addr.suffix || null, street: addr.street, street2: addr.street2 || "", city: addr.city, state: addr.state, zip: addr.zip };
    }
    return { name: form.name, first_name: form.firstName || null, middle_name: form.middleName || null, last_name: form.lastName || null, suffix: form.suffix || null, street: form.address, street2: form.address2, city: form.city, state: form.state, zip: form.zip };
  })();

  const resolvedBillingAddress = (() => {
    if (billingMode === "same") return null;
    if (billingMode === "saved") {
      const addr = savedAddresses.find(a => a.id === selectedBillingAddrId);
      if (addr) return { name: addr.name, first_name: addr.first_name || null, middle_name: addr.middle_name || null, last_name: addr.last_name || null, suffix: addr.suffix || null, street: addr.street, street2: addr.street2 || "", city: addr.city, state: addr.state, zip: addr.zip };
    }
    return { name: billingForm.name, first_name: billingForm.firstName || null, middle_name: billingForm.middleName || null, last_name: billingForm.lastName || null, suffix: billingForm.suffix || null, street: billingForm.address, street2: billingForm.address2 || "", city: billingForm.city, state: billingForm.state, zip: billingForm.zip };
  })();

  if (step === 4) {
    const ct = confirmedTotals || { total: 0 };
    const confirmedOrderId = sessionStorage.getItem("lastOrderId") || "";
    const sanitize = (s) => String(s || "").replace(/[<>"'&]/g, "");
    const paymentInstructions = {
      venmo: { label: "Venmo", handle: sanitize(paymentHandles.venmo_handle), instruction: `Open Venmo and send $${ct.total.toFixed(2)} to ${sanitize(paymentHandles.venmo_handle)}. Include your order ID in the note.` },
      cashapp: { label: "CashApp", handle: sanitize(paymentHandles.cashapp_handle), instruction: `Open CashApp and send $${ct.total.toFixed(2)} to ${sanitize(paymentHandles.cashapp_handle)}. Include your order ID in the note.` },
      zelle: { label: "Zelle", handle: sanitize(paymentHandles.zelle_handle), instruction: `Open your bank app or the Zelle app and send $${ct.total.toFixed(2)} to ${sanitize(paymentHandles.zelle_handle)}. Include your order ID in the note.` },
    };
    const pi = paymentInstructions[paymentMethod];
    return (
      <div style={{ maxWidth: 520, margin: "80px auto", padding: "0 24px" }} className="fade-up">
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: "56px", marginBottom: 16 }}>✓</div>
          <h1 style={{ fontSize: "36px", marginBottom: 8 }}>Order placed!</h1>
          {confirmedOrderId && (
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "13px", color: "var(--amber)", letterSpacing: "0.08em", marginBottom: 8 }}>
              ORDER ID: {confirmedOrderId}
            </p>
          )}
          <p style={{ color: "var(--text-dim)" }}>
            {paymentMethod === "card"
              ? `Your order is confirmed and being processed.`
              : `A confirmation email with payment instructions has been sent to ${user?.email}.`}
          </p>
        </div>
        {paymentMethod === "card" ? (
          <div style={{ background: "var(--surface)", border: "1px solid var(--green)", borderRadius: "var(--radius)", padding: 24, marginBottom: 24 }}>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--green)", letterSpacing: "0.1em", marginBottom: 8 }}>PAYMENT RECEIVED (MOCK)</p>
            <p style={{ fontSize: "13px", color: "var(--text-dim)", lineHeight: 1.6 }}>Credit card payment was accepted. Your order is now processing and will ship soon.</p>
          </div>
        ) : pi && (
          <div style={{ background: "var(--surface)", border: "1px solid var(--amber-dim)", borderRadius: "var(--radius)", padding: 24, marginBottom: 24 }}>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--amber)", letterSpacing: "0.1em", marginBottom: 12 }}>PAYMENT INSTRUCTIONS</p>
            <p style={{ fontSize: "16px", fontWeight: 700, color: "var(--text)", marginBottom: 16 }}>via {pi.label}</p>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.08em", marginBottom: 4 }}>SEND TO</p>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "22px", color: "var(--amber)", fontWeight: 700, marginBottom: 16, wordBreak: "break-all" }}>{pi.handle}</p>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.08em", marginBottom: 4 }}>AMOUNT</p>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "22px", color: "var(--text)", fontWeight: 700, marginBottom: 16 }}>${ct.total.toFixed(2)}</p>
            <div style={{ maxWidth: 300, margin: "0 auto 16px", textAlign: "left" }}>
              {[
                ["Subtotal", ct.subtotal],
                ...(ct.bulkSavings > 0 ? [["10-pack savings", -ct.bulkSavings, "var(--green)"]] : []),
                ...(ct.discount > 0 ? [["Discount", -ct.discount, "var(--green)"]] : []),
                ["Shipping", ct.shipping],
                ...(ct.storeCreditToApply > 0 ? [["Store credit", -ct.storeCreditToApply, "var(--green)"]] : []),
              ].map(([label, amount, color]) => <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 5, color: color || "var(--text-dim)" }}><span>{label}</span><span style={{ fontFamily: "var(--font-mono)" }}>{amount < 0 ? "−" : ""}${Math.abs(amount).toFixed(2)}</span></div>)}
            </div>
            <p style={{ fontSize: "13px", color: "var(--text-dim)", lineHeight: 1.6 }}>{pi.instruction}</p>
            <p style={{ fontSize: "13px", color: "var(--text-dim)", lineHeight: 1.6, marginTop: 8 }}>Your order will ship once payment is confirmed.</p>
          </div>
        )}
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <Btn onClick={() => { if (setInitialAccountTab) setInitialAccountTab("orders"); setView("account"); }}>View Orders</Btn>
          <Btn variant="ghost" onClick={() => setView("shop")}>Continue Shopping</Btn>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "48px 24px" }} className="fade-up">
      <h1 style={{ fontSize: "36px", marginBottom: 8 }}>Checkout</h1>
      <div style={{ display: "flex", gap: 8, marginBottom: 32 }}>
        {["Shipping", "Payment Method", "Review"].map((s, i) => (
          <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", background: step > i + 1 ? "var(--green)" : step === i + 1 ? "var(--amber)" : "var(--surface2)", color: step >= i + 1 ? "#111" : "var(--text-dim)", fontFamily: "var(--font-mono)" }}>{step > i + 1 ? "✓" : i + 1}</span>
            <span style={{ fontSize: "13px", color: step >= i + 1 ? "var(--text)" : "var(--text-muted)" }}>{s}</span>
            {i < 2 && <span style={{ color: "var(--text-muted)", margin: "0 4px" }}>→</span>}
          </div>
        ))}
      </div>

      <div className="r-grid" style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 32 }}>
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 28 }}>
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <h3 style={{ fontSize: "20px", marginBottom: 4 }}>Shipping Address</h3>

              {addressesLoading ? (
                <p style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--text-muted)", letterSpacing: "0.1em" }}>LOADING ADDRESSES…</p>
              ) : savedAddresses.length > 0 ? (
                <>
                  {/* Toggle: saved vs new */}
                  <div style={{ display: "flex", gap: 0, border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
                    {[["saved", "Saved Address"], ["new", "New Address"]].map(([id, label]) => (
                      <button key={id} onClick={() => { setAddressMode(id); setShippingError(""); if (id === "new") { setForm(f => ({ ...f, address: "", address2: "", city: "", state: "", zip: "" })); } }} style={{
                        flex: 1, padding: "9px", fontSize: "12px", fontWeight: 700,
                        background: addressMode === id ? "var(--amber)" : "transparent",
                        color: addressMode === id ? "#111" : "var(--text-dim)",
                        transition: "all 0.15s",
                      }}>{label}</button>
                    ))}
                  </div>

                  {addressMode === "saved" ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {savedAddresses.map(addr => (
                        <button key={addr.id} onClick={() => selectAddress(addr)} style={{
                          textAlign: "left", padding: "14px 16px",
                          background: selectedAddrId === addr.id ? "rgba(232, 160, 32, 0.08)" : "var(--surface2)",
                          border: `2px solid ${selectedAddrId === addr.id ? "var(--amber)" : "var(--border)"}`,
                          borderRadius: "var(--radius)", cursor: "pointer", transition: "all 0.15s",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <span style={{
                              width: 16, height: 16, borderRadius: "50%",
                              border: `2px solid ${selectedAddrId === addr.id ? "var(--amber)" : "var(--border)"}`,
                              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>
                              {selectedAddrId === addr.id && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--amber)" }} />}
                            </span>
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: "11px", fontWeight: 700, color: "var(--amber)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                              {addr.label || "Address"}
                            </span>
                            {addr.is_default && (
                              <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", background: "rgba(232, 160, 32, 0.15)", color: "var(--amber)", padding: "2px 6px", borderRadius: 2 }}>DEFAULT</span>
                            )}
                          </div>
                          <p style={{ fontSize: "14px", color: "var(--text)", marginBottom: 2, marginLeft: 24 }}>{addr.name}</p>
                          <p style={{ fontSize: "13px", color: "var(--text-dim)", lineHeight: 1.5, marginLeft: 24 }}>
                            {addr.street}, {addr.city}, {addr.state} {addr.zip}
                          </p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    /* New address form */
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <NamePartsFields value={form} onChange={setShipName} />
                      <ManualToggle active={shipManualEntry} onToggle={() => setShipManualEntry(m => !m)} />
                      <AddressInput
                        values={{ street: form.address, street2: form.address2, city: form.city, state: form.state, zip: form.zip }}
                        onChange={u => setForm(f => ({ ...f, ...(u.street != null && { address: u.street }), ...(u.street2 != null && { address2: u.street2 }), ...(u.city != null && { city: u.city }), ...(u.state != null && { state: u.state }), ...(u.zip != null && { zip: u.zip }) }))}
                        manualMode={shipManualEntry}
                      />
                      <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginTop: 4 }}>
                        <input type="checkbox" checked={saveNewAddr} onChange={e => setSaveNewAddr(e.target.checked)} style={{ width: "auto", accentColor: "var(--amber)", flexShrink: 0 }} />
                        <span style={{ fontSize: "13px", color: "var(--text-dim)" }}>Save this address for future orders</span>
                      </label>
                      {saveNewAddr && (
                        <input value={newAddrLabel} onChange={e => setNewAddrLabel(e.target.value)} placeholder="Address label (e.g. Home, Work, Office)" style={{ marginTop: -4 }} />
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* No saved addresses — show new address form directly */
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <NamePartsFields value={form} onChange={setShipName} />
                  <ManualToggle active={shipManualEntry} onToggle={() => setShipManualEntry(m => !m)} />
                  <AddressInput
                    values={{ street: form.address, street2: form.address2, city: form.city, state: form.state, zip: form.zip }}
                    onChange={u => setForm(f => ({ ...f, ...(u.street != null && { address: u.street }), ...(u.street2 != null && { address2: u.street2 }), ...(u.city != null && { city: u.city }), ...(u.state != null && { state: u.state }), ...(u.zip != null && { zip: u.zip }) }))}
                    manualMode={shipManualEntry}
                  />
                  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginTop: 4 }}>
                    <input type="checkbox" checked={saveNewAddr} onChange={e => setSaveNewAddr(e.target.checked)} style={{ width: "auto", accentColor: "var(--amber)", flexShrink: 0 }} />
                    <span style={{ fontSize: "13px", color: "var(--text-dim)" }}>Save this address for future orders</span>
                  </label>
                  {saveNewAddr && (
                    <input value={newAddrLabel} onChange={e => setNewAddrLabel(e.target.value)} placeholder="Address label (e.g. Home, Work, Office)" style={{ marginTop: -4 }} />
                  )}
                </div>
              )}

              {shippingError && <p style={{ color: "var(--red)", fontSize: "13px", fontFamily: "var(--font-mono)" }}>{shippingError}</p>}
              <Btn onClick={async () => { if (await validateAndSaveShipping()) { window.umami?.track("checkout-shipping-complete"); setStep(2); } }} style={{ marginTop: 8 }}>Select Payment Method →</Btn>
            </div>
          )}
          {step === 2 && (() => {
            const methods = [
              { key: "venmo", label: "Venmo", handle: paymentHandles.venmo_handle, enabled: paymentHandles.venmo_enabled },
              { key: "cashapp", label: "CashApp", handle: paymentHandles.cashapp_handle, enabled: paymentHandles.cashapp_enabled },
              { key: "zelle", label: "Zelle", handle: paymentHandles.zelle_handle, enabled: paymentHandles.zelle_enabled },
            ].filter(m => m.handle && m.enabled);
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <h3 style={{ fontSize: "20px", marginBottom: 4 }}>Select Payment Method</h3>
                {methods.length === 0 ? (
                  <p style={{ color: "var(--text-dim)", fontSize: "13px" }}>No payment methods configured. Contact the store admin.</p>
                ) : (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {methods.map(m => (
                      <button
                        key={m.key}
                        onClick={() => { setPaymentMethod(m.key); setPaymentError(""); }}
                        style={{
                          flex: 1, minWidth: 100, padding: "14px 12px", borderRadius: "var(--radius)",
                          border: `2px solid ${paymentMethod === m.key ? "var(--amber)" : "var(--border)"}`,
                          background: paymentMethod === m.key ? "rgba(232,160,32,0.08)" : "var(--surface2)",
                          color: paymentMethod === m.key ? "var(--amber)" : "var(--text-dim)",
                          fontWeight: 700, fontSize: "14px", cursor: "pointer", transition: "all 0.15s",
                        }}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                )}
                {paymentError && <p style={{ color: "var(--red)", fontSize: "13px", fontFamily: "var(--font-mono)" }}>{paymentError}</p>}

                {paymentMethod === "card" && (
                  <div style={{ marginTop: 4 }}>
                    <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)", letterSpacing: "0.08em", marginBottom: 10 }}>BILLING ADDRESS</p>
                    <div style={{ display: "flex", gap: 0, border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden", marginBottom: 14 }}>
                      {[["same", "Same as Shipping"], ["saved", "Saved Address"], ["new", "New Address"]].map(([id, label]) => (
                        <button key={id} onClick={() => { setBillingMode(id); setBillingError(""); }} style={{
                          flex: 1, padding: "9px 6px", fontSize: "11px", fontWeight: 700,
                          background: billingMode === id ? "var(--amber)" : "transparent",
                          color: billingMode === id ? "#111" : "var(--text-dim)",
                          transition: "all 0.15s",
                        }}>{label}</button>
                      ))}
                    </div>
                    {billingMode === "saved" && (
                      savedAddresses.length > 0 ? (
                        <select value={selectedBillingAddrId || ""} onChange={e => setSelectedBillingAddrId(e.target.value)} style={{ width: "100%" }}>
                          <option value="">Select an address…</option>
                          {savedAddresses.map(addr => (
                            <option key={addr.id} value={addr.id}>
                              {addr.label ? `${addr.label} — ` : ""}{addr.name}, {addr.street}, {addr.city}, {addr.state} {addr.zip}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p style={{ fontSize: "13px", color: "var(--text-dim)" }}>No saved addresses. Switch to "New Address" to enter one.</p>
                      )
                    )}
                    {billingMode === "new" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        <NamePartsFields value={billingForm} onChange={setBillName} />
                        <ManualToggle active={billManualEntry} onToggle={() => setBillManualEntry(m => !m)} />
                        <AddressInput
                          values={{ street: billingForm.address, street2: billingForm.address2, city: billingForm.city, state: billingForm.state, zip: billingForm.zip }}
                          onChange={u => setBillingForm(f => ({ ...f, ...(u.street != null && { address: u.street }), ...(u.street2 != null && { address2: u.street2 }), ...(u.city != null && { city: u.city }), ...(u.state != null && { state: u.state }), ...(u.zip != null && { zip: u.zip }) }))}
                          manualMode={billManualEntry}
                        />
                        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                          <input type="checkbox" checked={saveBillingAddr} onChange={e => setSaveBillingAddr(e.target.checked)} style={{ width: "auto", accentColor: "var(--amber)", flexShrink: 0 }} />
                          <span style={{ fontSize: "13px", color: "var(--text-dim)" }}>Save this address to my account</span>
                        </label>
                        {saveBillingAddr && (
                          <input value={billingAddrLabel} onChange={e => setBillingAddrLabel(e.target.value)} placeholder="Address label (e.g. Home, Work)" />
                        )}
                      </div>
                    )}
                    {billingError && <p style={{ color: "var(--red)", fontSize: "13px", fontFamily: "var(--font-mono)", marginTop: 8 }}>{billingError}</p>}
                  </div>
                )}

                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  <Btn variant="ghost" onClick={() => setStep(1)}>← Back</Btn>
                  <Btn onClick={async () => {
                    if (!paymentMethod) { setPaymentError("Please select a payment method."); return; }
                    if (paymentMethod === "card") {
                      if (billingMode === "saved" && !selectedBillingAddrId) {
                        setBillingError("Please select a billing address."); return;
                      }
                      if (billingMode === "new") {
                        if (!(billingForm.firstName || "").trim() || !(billingForm.lastName || "").trim() || !billingForm.address.trim() || !billingForm.city.trim() || !billingForm.state || !billingForm.zip.trim()) {
                          setBillingError("All billing address fields are required."); return;
                        }
                        if (!/^\d{5}$/.test(billingForm.zip.trim())) { setBillingError("Billing ZIP must be 5 digits."); return; }
                        if (saveBillingAddr) {
                          if (!billingAddrLabel.trim()) { setBillingError("Please enter a label for the billing address."); return; }
                          if (user?.id) {
                            const isFirst = savedAddresses.length === 0;
                            await insertAddressDeduped({
                              label: billingAddrLabel.trim(),
                              name: billingForm.name.trim(),
                              first_name: (billingForm.firstName || "").trim() || null,
                              middle_name: (billingForm.middleName || "").trim() || null,
                              last_name: (billingForm.lastName || "").trim() || null,
                              suffix: (billingForm.suffix || "").trim() || null,
                              street: billingForm.address.trim(),
                              street2: billingForm.address2.trim() || null,
                              city: billingForm.city.trim(),
                              state: billingForm.state,
                              zip: billingForm.zip.trim(),
                              user_id: user.id,
                              is_default: isFirst,
                            });
                          }
                        }
                      }
                    }
                    window.umami?.track("checkout-payment-selected", { method: paymentMethod });
                    setStep(3);
                  }}>Review Order →</Btn>
                </div>
              </div>
            );
          })()}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <h3 style={{ fontSize: "20px", marginBottom: 4 }}>Review Your Order</h3>

              {/* Shipping address summary */}
              <div style={{ background: "var(--surface2)", borderRadius: "var(--radius)", padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--amber)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Shipping To</span>
                  <button onClick={() => setStep(1)} style={{ fontSize: "12px", color: "var(--amber)", fontFamily: "var(--font-mono)" }}>Edit</button>
                </div>
                <p style={{ fontSize: "14px", fontWeight: 600, marginBottom: 2 }}>{resolvedAddress.name}</p>
                <p style={{ fontSize: "13px", color: "var(--text-dim)", lineHeight: 1.5 }}>
                  {resolvedAddress.street}{resolvedAddress.street2 ? `, ${resolvedAddress.street2}` : ""}<br />
                  {resolvedAddress.city}, {resolvedAddress.state} {resolvedAddress.zip}
                </p>
              </div>

              {/* Payment summary */}
              <div style={{ background: "var(--surface2)", borderRadius: "var(--radius)", padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--amber)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Payment</span>
                  <button onClick={() => setStep(2)} style={{ fontSize: "12px", color: "var(--amber)", fontFamily: "var(--font-mono)" }}>Edit</button>
                </div>
                <p style={{ fontSize: "13px", color: "var(--text-dim)", wordBreak: "break-all" }}>
                  {paymentMethod === "venmo" && `Venmo — ${paymentHandles.venmo_handle}`}
                  {paymentMethod === "cashapp" && `CashApp — ${paymentHandles.cashapp_handle}`}
                  {paymentMethod === "zelle" && `Zelle — ${paymentHandles.zelle_handle}`}
                  {paymentMethod === "card" && "Credit Card (mock)"}
                </p>
              </div>

              {/* Billing address summary (card only) */}
              {paymentMethod === "card" && (() => {
                const addr = resolvedBillingAddress ?? resolvedAddress;
                return (
                  <div style={{ background: "var(--surface2)", borderRadius: "var(--radius)", padding: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--amber)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Billing Address</span>
                      <button onClick={() => setStep(2)} style={{ fontSize: "12px", color: "var(--amber)", fontFamily: "var(--font-mono)" }}>Edit</button>
                    </div>
                    <p style={{ fontSize: "14px", fontWeight: 600, marginBottom: 2 }}>{addr.name}</p>
                    <p style={{ fontSize: "13px", color: "var(--text-dim)", lineHeight: 1.5 }}>
                      {addr.street}<br />
                      {addr.city}, {addr.state} {addr.zip}
                    </p>
                  </div>
                );
              })()}

              {/* Items summary */}
              <div style={{ background: "var(--surface2)", borderRadius: "var(--radius)", padding: 16 }}>
                <span style={{ fontSize: "12px", fontFamily: "var(--font-mono)", color: "var(--amber)", textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 10 }}>Items ({cart.reduce((s, i) => s + i.qty, 0)})</span>
                {cart.map(item => (
                  <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {getPrimaryImage(item) && <img src={getPrimaryImage(item)} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />}
                      <div>
                        <p style={{ fontSize: "13px", fontWeight: 600 }}>{item.name}</p>
                        <p style={{ fontSize: "12px", color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>Qty: {item.qty}</p>
                      </div>
                    </div>
                    <span style={{ fontSize: "13px", fontFamily: "var(--font-mono)" }}>${(item.price * item.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Coupon input in review step */}
              <div>
                {appliedCoupon ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(74,158,106,0.1)", border: "1px solid var(--green)", borderRadius: "var(--radius)", padding: "8px 12px" }}>
                      <div>
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--green)", fontWeight: 700 }}>{appliedCoupon.code}</span>
                        <span style={{ fontSize: "12px", color: "var(--text-dim)", marginLeft: 8 }}>
                          {appliedCoupon.type === "percent" ? `${appliedCoupon.value}% off` : appliedCoupon.type === "fixed" ? `$${Number(appliedCoupon.value).toFixed(2)} off` : "Free shipping"}
                        </span>
                      </div>
                      <button onClick={() => { setAppliedCoupon(null); }} style={{ fontSize: "12px", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.color = "var(--red)"}
                        onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}>Remove</button>
                    </div>
                    {appliedCoupon.isAffiliate && (
                      <div style={{ fontSize: "11px", color: "var(--text-dim)", fontFamily: "var(--font-mono)", padding: "0 4px" }}>
                        🤝 Referred by an affiliate — you save {appliedCoupon.value}%
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        value={couponInput}
                        onChange={e => { setCouponInput(e.target.value); setCouponInputError(""); }}
                        onKeyDown={e => e.key === "Enter" && handleApplyCouponCheckout()}
                        placeholder="Coupon code"
                        style={{ flex: 1, fontSize: "13px", padding: "7px 10px" }}
                      />
                      <Btn small disabled={couponInputLoading || !couponInput.trim()} onClick={handleApplyCouponCheckout}>
                        {couponInputLoading ? "…" : "Apply"}
                      </Btn>
                    </div>
                    {couponInputError && <p style={{ color: "var(--red)", fontSize: "12px", fontFamily: "var(--font-mono)", marginTop: 6 }}>{couponInputError}</p>}
                  </div>
                )}
              </div>

              {/* Store credit */}
              {storeCreditBalance > 0 && (
                <div style={{ borderTop: "1px solid var(--border)", paddingTop: 14, marginBottom: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "13px", whiteSpace: "nowrap" }}>
                      <input type="checkbox" checked={useStoreCredit} onChange={e => setUseStoreCredit(e.target.checked)} style={{ width: "auto", flexShrink: 0 }} />
                      <span>Apply store credit</span>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--green)" }}>(${storeCreditBalance.toFixed(2)})</span>
                    </label>
                    {useStoreCredit && (
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: "13px", color: "var(--green)", flexShrink: 0 }}>−${storeCreditToApply.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Order total */}
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 6 }}>
                  <span style={{ color: "var(--text-dim)" }}>Subtotal</span>
                  <span style={{ fontFamily: "var(--font-mono)" }}>${subtotal.toFixed(2)}</span>
                </div>
                {bulkSavings > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13, marginBottom: 6 }}>
                    <span style={{ color: "var(--green)" }}>10-pack savings (20%)<small style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 10.5, opacity: 0.75 }}>{discountedUnits === eligibleUnits ? `${discountedUnits} vials` : `${discountedUnits} of ${eligibleUnits} vials · lowest-priced first`}</small></span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "var(--green)" }}>−${bulkSavings.toFixed(2)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 6 }}>
                      <span style={{ color: "var(--green)" }}>{appliedCoupon?.isAffiliate ? `Referral discount (${appliedCoupon.code})` : `Discount (${appliedCoupon.code})`}</span>
                      <span style={{ fontFamily: "var(--font-mono)", color: "var(--green)" }}>−${discount.toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 6 }}>
                      <span style={{ color: "var(--text-dim)" }}>Discounted subtotal</span>
                      <span style={{ fontFamily: "var(--font-mono)" }}>${(postBulk - discount).toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 6 }}>
                  <span style={{ color: "var(--text-dim)" }}>Shipping</span>
                  <span style={{ fontFamily: "var(--font-mono)", color: shipping === 0 ? "var(--green)" : "inherit" }}>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span>
                </div>
                {storeCreditToApply > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 6 }}>
                    <span style={{ color: "var(--green)" }}>Store Credit</span>
                    <span style={{ fontFamily: "var(--font-mono)", color: "var(--green)" }}>−${storeCreditToApply.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "16px", marginTop: 8 }}>
                  <span>Total</span>
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--amber)" }}>${total.toFixed(2)}</span>
                </div>
              </div>

              {orderError && (
                <div style={{ background: "var(--red-bg, #fff0f0)", border: "1px solid var(--red, #e53e3e)", borderRadius: "8px", padding: "12px 16px", fontSize: "13px", fontFamily: "var(--font-mono)" }}>
                  <p style={{ color: "var(--red, #e53e3e)", fontWeight: 600, margin: "0 0 4px 0" }}>{orderError.title}</p>
                  <p style={{ color: "var(--text-secondary, #666)", margin: 0 }}>{orderError.message}</p>
                </div>
              )}
              <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
                <Btn variant="ghost" onClick={() => setStep(2)}>← Back</Btn>
                <Btn disabled={loading} onClick={handlePlaceOrder}>
                  {loading ? "Placing Order…" : "Place Order"}
                </Btn>
              </div>
            </div>
          )}
        </div>

        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 24, height: "fit-content" }}>
          <h3 style={{ fontSize: "18px", marginBottom: 16 }}>Order Summary</h3>
          {cart.map(i => (
            <div key={i.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: "13px" }}>
              <span style={{ color: "var(--text-dim)" }}>{i.name} ×{i.qty}</span>
              <span style={{ fontFamily: "var(--font-mono)" }}>${(i.price * i.qty).toFixed(2)}</span>
            </div>
          ))}
          <div style={{ borderTop: "1px solid var(--border)", marginTop: 12, paddingTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 8 }}>
              <span style={{ color: "var(--text-dim)" }}>Subtotal</span>
              <span style={{ fontFamily: "var(--font-mono)" }}>${subtotal.toFixed(2)}</span>
            </div>
            {bulkSavings > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 13, marginBottom: 8 }}>
                <span style={{ color: "var(--green)" }}>10-pack savings (20%)<small style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 10.5, opacity: 0.75 }}>{discountedUnits === eligibleUnits ? `${discountedUnits} vials` : `${discountedUnits} of ${eligibleUnits} vials · lowest-priced first`}</small></span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--green)" }}>−${bulkSavings.toFixed(2)}</span>
              </div>
            )}
            {discount > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 8 }}>
                  <span style={{ color: "var(--green)" }}>{appliedCoupon?.isAffiliate ? `Referral discount (${appliedCoupon.code})` : `Discount (${appliedCoupon.code})`}</span>
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--green)" }}>−${discount.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 8 }}>
                  <span style={{ color: "var(--text-dim)" }}>Discounted subtotal</span>
                  <span style={{ fontFamily: "var(--font-mono)" }}>${(postBulk - discount).toFixed(2)}</span>
                </div>
              </>
            )}
            {storeCreditToApply > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 8 }}>
                <span style={{ color: "var(--green)" }}>Store Credit</span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--green)" }}>−${storeCreditToApply.toFixed(2)}</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: 8 }}>
              <span style={{ color: "var(--text-dim)" }}>Shipping</span>
              <span style={{ fontFamily: "var(--font-mono)", color: shipping === 0 ? "var(--green)" : "inherit" }}>{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
              <span>Total</span>
              <span style={{ fontFamily: "var(--font-mono)", color: "var(--amber)", fontSize: "18px" }}>${total.toFixed(2)}</span>
            </div>
          </div>
          {paymentMethod && (
            <div style={{ borderTop: "1px solid var(--border)", marginTop: 16, paddingTop: 16 }}>
              <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.08em", marginBottom: 8 }}>PAYMENT METHOD</p>
              {paymentMethod === "card" ? (
                <p style={{ fontSize: "13px", color: "var(--text-dim)" }}>Credit Card (mock)</p>
              ) : (() => {
                const label = { venmo: "Venmo", cashapp: "CashApp", zelle: "Zelle" }[paymentMethod];
                const handle = paymentHandles[`${paymentMethod}_handle`];
                return (
                  <>
                    <p style={{ fontSize: "13px", color: "var(--text)", fontWeight: 600, marginBottom: 6 }}>{label}</p>
                    {handle && (
                      <>
                        <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.08em", marginBottom: 2 }}>SEND TO</p>
                        <p style={{ fontFamily: "var(--font-mono)", fontSize: "15px", color: "var(--amber)", fontWeight: 700, marginBottom: 8 }}>{handle}</p>
                        <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", letterSpacing: "0.08em", marginBottom: 2 }}>AMOUNT</p>
                        <p style={{ fontFamily: "var(--font-mono)", fontSize: "15px", color: "var(--text)", fontWeight: 700 }}>${total.toFixed(2)}</p>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutView;
